"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Branded autoplay player for the 9:16 UGC intro clips (landing + result).
 *
 * Why not a native `<video controls>`? On iOS the native control bar and the
 * fullscreen takeover look unbranded and platform-specific (the "iPhone frame"
 * with Apple's own buttons). The professional pattern for a hero/intro clip is
 * a muted, inline, looping autoplay video with NO native controls plus a small
 * branded sound toggle:
 *   - `muted + autoPlay + playsInline + loop` → autoplays inline on iOS/Android
 *     WITHOUT hijacking to the native fullscreen player. `playsInline` is the
 *     key iOS bit; `muted` is required for autoplay on every browser.
 *   - no `controls`            → no native scrubber / frame.
 *   - `disableRemotePlayback` + `x-webkit-airplay="deny"` → no AirPlay/cast chip.
 *   - tapping the surface (or the speaker chip) toggles sound — matching the
 *     "Tap for sound" caption the callers show.
 *
 * `allowFullscreen` (opt-in, long-form clips) adds a branded expand chip. The
 * no-native-controls rule has one deliberate exception there: WHILE fullscreen
 * the native controls are enabled (a 90s+ walkthrough needs pause/scrub), and
 * stripped again on exit so the inline player stays chrome-free. On iPhone
 * Safari there is no element fullscreen API — `webkitEnterFullscreen()` hands
 * off to the native player, which ships its own controls.
 *
 * `onError` still hides the player (stale/blocked source) so a broken URL never
 * leaves a dead frame or pollutes the console (Lighthouse TBT/CLS).
 */
interface ClientVideoProps {
  src: string;
  poster?: string;
  className?: string;
  "data-testid"?: string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  playsInline?: boolean;
  preload?: string;
  /** Branded fullscreen chip for long-form clips. Default off. */
  allowFullscreen?: boolean;
}

/** iOS Safari's prefixed video-fullscreen surface (no element fullscreen API on iPhone). */
type FullscreenCapableVideo = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
  webkitDisplayingFullscreen?: boolean;
};

function isVideoFullscreen(v: FullscreenCapableVideo): boolean {
  return document.fullscreenElement === v || v.webkitDisplayingFullscreen === true;
}

export function ClientVideo({
  src,
  poster,
  className,
  "data-testid": testId,
  loop = true,
  playsInline = true,
  preload = "none",
  allowFullscreen = false,
}: ClientVideoProps) {
  const ref = useRef<HTMLVideoElement>(null);
  const [failed, setFailed] = useState(false);
  const [muted, setMuted] = useState(true);
  const [interacted, setInteracted] = useState(false);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    // Guarantee muted at mount so iOS/Chrome honor autoplay, and strip the
    // remote-playback (AirPlay/cast) affordance the native UA would add.
    v.muted = true;
    try {
      (v as HTMLVideoElement & { disableRemotePlayback?: boolean }).disableRemotePlayback =
        true;
    } catch {
      /* not supported — harmless */
    }
    v.setAttribute("x-webkit-airplay", "deny");

    // Lazy playback: with preload="none" nothing loads until the clip scrolls
    // into view — then we play (a muted play() needs no gesture) and pause when
    // it leaves, so a heavy hero video costs nothing on initial load. The poster
    // paints the first frame meanwhile.
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) void v.play().catch(() => {});
          // Never pause a fullscreen playback — on iOS the fullscreen video
          // can report as non-intersecting while the native player is up.
          else if (!isVideoFullscreen(v)) v.pause();
        }
      },
      { rootMargin: "200px 0px", threshold: 0.25 },
    );
    io.observe(v);

    // Fullscreen exit cleanup: native controls are only for the fullscreen
    // takeover — strip them when the user leaves so the inline player goes
    // back to the branded chrome-free surface.
    const onFullscreenChange = () => {
      if (document.fullscreenElement !== v) v.removeAttribute("controls");
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => {
      io.disconnect();
      document.removeEventListener("fullscreenchange", onFullscreenChange);
    };
  }, []);

  if (failed) return null;

  function toggleSound() {
    const v = ref.current;
    if (!v) return;
    const nextMuted = !v.muted;
    v.muted = nextMuted;
    if (!nextMuted) void v.play().catch(() => {});
    setMuted(nextMuted);
    setInteracted(true);
  }

  function enterFullscreen() {
    const v = ref.current as FullscreenCapableVideo | null;
    if (!v) return;
    // Expanding is explicit watch intent — unmute in the same user gesture.
    v.muted = false;
    setMuted(false);
    setInteracted(true);
    if (typeof v.requestFullscreen === "function") {
      v.setAttribute("controls", "");
      // Promise.resolve guards engines where requestFullscreen returns void.
      void Promise.resolve(v.requestFullscreen()).catch(() =>
        v.removeAttribute("controls"),
      );
    } else if (typeof v.webkitEnterFullscreen === "function") {
      v.webkitEnterFullscreen();
    }
    void v.play().catch(() => {});
  }

  return (
    <>
      <video
        ref={ref}
        className={className}
        src={src}
        poster={poster}
        muted={muted}
        loop={loop}
        playsInline={playsInline}
        preload={preload}
        data-testid={testId}
        onError={(e) => {
          // Stale CDN asset sometimes 4xx's — fail silently rather than dirty
          // the console. Removing src stops the native error bubbling further.
          setFailed(true);
          e.currentTarget.removeAttribute("src");
        }}
      >
        <track kind="captions" />
      </video>

      {/* Branded control layer — tap anywhere to toggle sound. */}
      <button
        type="button"
        onClick={toggleSound}
        aria-label={muted ? "Unmute video" : "Mute video"}
        aria-pressed={!muted}
        className="group absolute inset-0 flex items-end justify-end p-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
      >
        {/* "Tap for sound" hint — shown until the first unmute. */}
        {!interacted && muted ? (
          <span className="pointer-events-none absolute bottom-3 left-3 rounded-full bg-black/45 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur-sm">
            Tap for sound
          </span>
        ) : null}

        {/* Speaker chip — always visible, reflects mute state. */}
        <span className="pointer-events-none flex h-9 w-9 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm transition group-hover:bg-black/60">
          {muted ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M11 5 6 9H3v6h3l5 4V5z" fill="currentColor" />
              <path d="m16 9 4 6M20 9l-4 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M11 5 6 9H3v6h3l5 4V5z" fill="currentColor" />
              <path d="M15.5 8.5a5 5 0 0 1 0 7M18 6a8 8 0 0 1 0 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          )}
        </span>
      </button>

      {/* Fullscreen chip — opt-in, sits above the sound-toggle surface. */}
      {allowFullscreen ? (
        <button
          type="button"
          onClick={enterFullscreen}
          aria-label="Watch fullscreen"
          data-testid={testId ? `${testId}-fullscreen` : undefined}
          className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm transition hover:bg-black/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M9 4H4v5M15 4h5v5M9 20H4v-5M15 20h5v-5"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ) : null}
    </>
  );
}
